import { useId } from 'react'

export interface ChoiceOption<T extends string | number> {
  value: T
  label: string
  /** Shown beside the label, not only to a screen reader. */
  hint?: string
}

interface ChoiceChipsProps<T extends string | number> {
  /** The group's accessible name, rendered visibly. */
  label: string
  options: readonly ChoiceOption<T>[]
  /** `null` is **unanswered**, and it is a real state rather than a placeholder. */
  value: T | null
  /** `null` means the user withdrew the answer. */
  onChange: (value: T | null) => void
  hint?: string
  disabled?: boolean
  /**
   * The options cannot be chosen, but a held answer can still be withdrawn.
   *
   * `disabled` also hides "No answer", which is right for a question that may
   * not be touched at all. This is the narrower lock JOURNAL-FIX-01 needs: a
   * value that contradicts another answer (an intensity after "Exercised: No")
   * stays on screen -- it is not deleted behind the user's back -- and the only
   * move left is to clear it. With `value === null` it behaves as `disabled`.
   */
  clearOnly?: boolean
  /**
   * The id of an element that explains the control's current state.
   *
   * `NumberStepper` already takes one, for the same reason and from the same
   * caller: a lock that changes what a control will accept has to be announced,
   * and the sentence lives in the form's own live region rather than here.
   * Appended to any `hint`, never instead of it.
   */
  describedBy?: string
  /**
   * How the options fill their row.
   *
   * `wrap` (the default) lets five options with visible hints grow to the space
   * they have and wrap when they run out; `scale` lays ten numbered steps out as
   * one ten-column grid. Both are pure CSS on the container -- the role, the
   * buttons, the names and `aria-pressed` are identical, because a ten-step
   * scale is still "choose one, or none" and not a slider.
   */
  layout?: 'wrap' | 'scale'
}

/**
 * Choose one, or none.
 *
 * ── WHY NOT `SegmentedControl` ──────────────────────────────────────────────
 *
 * Two reasons, and the second is the one that matters. `.segmented` is a
 * `display: flex` bar of `flex: 1` options, which is right for two or three short
 * words and wrong for five options carrying visible hints or ten numbered chips
 * at 360px. And `SegmentedControl`'s `onChange` is `(value: T) => void` -- there
 * is no way to express "I withdraw that", so an answer captured on the device
 * could be changed here but never removed.
 *
 * What it DOES keep is that component's documented reasoning: `role="group"` with
 * `aria-pressed` buttons rather than a radiogroup, because a radiogroup's roving
 * focus is the pattern for a field with a default and these have none.
 *
 * ── "No answer" IS THE DEVICE'S WORD ────────────────────────────────────────
 *
 * `calibrations/static/main.js` offers `<option value="">No answer</option>` on
 * every journal select. Reusing it keeps one vocabulary across the two places a
 * person can answer the same question.
 */
export function ChoiceChips<T extends string | number>({
  label,
  options,
  value,
  onChange,
  hint,
  disabled = false,
  clearOnly = false,
  describedBy: extraDescription,
  layout = 'wrap',
}: ChoiceChipsProps<T>) {
  const labelId = useId()
  const hintId = `${labelId}-hint`
  const optionsDisabled = disabled || clearOnly
  const describedBy = [hint ? hintId : null, extraDescription ?? null].filter(Boolean).join(' ')

  return (
    <div className="choice">
      <div className="choice__head">
        <span className="field__label" id={labelId}>
          {label}
        </span>
        {value !== null && !disabled && (
          <button
            type="button"
            className="btn btn--ghost btn--sm choice__clear"
            onClick={() => onChange(null)}
          >
            No answer<span className="visually-hidden"> for {label}</span>
          </button>
        )}
      </div>
      <div
        className={layout === 'scale' ? 'chipset chipset--scale' : 'chipset chipset--grow'}
        role="group"
        aria-labelledby={labelId}
        aria-describedby={describedBy || undefined}
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className="chip"
            aria-pressed={value === option.value}
            disabled={optionsDisabled}
            onClick={() => onChange(option.value)}
          >
            <span className="chip__label">{option.label}</span>
            {option.hint && <span className="chip__hint">{option.hint}</span>}
          </button>
        ))}
      </div>
      {hint && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
    </div>
  )
}
